/**
 * CertificatesPage.tsx — Compliance Certificates View.
 *
 * Provides digital DPDP compliance certificate generation, verification, and PDF download.
 */

import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../components/ui/Toast';
import { SkeletonCard } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { Spinner } from '../../components/ui/Spinner';
import { apiFetch, getNetworkErrorMessage } from '../../lib/api';
import type { ConsentRecord } from '../../lib/types';
import { STORAGE_KEYS } from '../../lib/constants';

export function CertificatesPage() {
  const { addToast } = useToast();
  const [consents, setConsents] = useState<ConsentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeAppId = localStorage.getItem(STORAGE_KEYS.APP_ID) || '';

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ consents: ConsentRecord[] }>('/v1/consents', {
        useApiKey: true,
      });
      setConsents(data.consents || []);
    } catch (err) {
      setError(getNetworkErrorMessage(err, 'Failed to check compliance certificate status.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownloadPdf = async () => {
    if (!activeAppId) {
      addToast({ type: 'error', message: 'No active application selected.' });
      return;
    }

    setIsGenerating(true);
    try {
      const blob = await apiFetch<Blob>(`/v1/certificates/${activeAppId}`, {
        useApiKey: true,
        blob: true,
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `dpdp_compliance_certificate_${activeAppId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);

      addToast({ type: 'success', message: 'DPDP Compliance Certificate PDF downloaded!' });
    } catch (err) {
      addToast({
        type: 'error',
        message: getNetworkErrorMessage(err, 'Certificate PDF generation failed.'),
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">
          Compliance Certificates
        </h1>
        <p className="mt-1 text-sm text-sub">
          Cryptographically signed DPDP Section 6 compliance proofs and downloadable PDF audit reports.
        </p>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="rounded-xl border border-err/30 bg-err/10 p-4 flex items-center justify-between text-sm text-err">
          <span>⚠️ {error}</span>
          <button type="button" onClick={fetchData} className="text-xs font-bold underline hover:no-underline ml-4">
            Retry
          </button>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <SkeletonCard />
      ) : consents.length === 0 ? (
        <EmptyState
          icon="📜"
          title="No Certificates Available"
          description="Compliance certificates will be unlocked once your application records user consent transactions."
        />
      ) : (
        <div className="space-y-6">
          {/* Certificate Card */}
          <div className="rounded-2xl border border-line bg-surface p-8 shadow-card space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-line pb-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center text-2xl font-bold shadow-sm">
                  ✓
                </div>
                <div>
                  <h3 className="text-lg font-bold text-ink">DPDP Section 6 Certificate Ready</h3>
                  <p className="text-xs text-sub">
                    Cryptographic audit trail backed by Cloudflare Durable Objects.
                  </p>
                </div>
              </div>

              <span className="inline-flex items-center gap-1.5 rounded-full border border-ok/30 bg-ok/10 px-3 py-1 text-xs font-bold text-ok">
                <span className="h-2 w-2 rounded-full bg-ok animate-pulse" />
                VERIFIED COMPLIANT
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div className="rounded-xl border border-line bg-card p-4">
                <span className="text-muted block text-[10px] uppercase font-bold">Total Recorded Consents</span>
                <span className="text-ink text-2xl font-extrabold mt-1 block">{consents.length}</span>
              </div>
              <div className="rounded-xl border border-line bg-card p-4">
                <span className="text-muted block text-[10px] uppercase font-bold">HMAC Audit Signature</span>
                <span className="text-ok font-mono text-xs font-bold mt-2 block truncate">
                  SHA-256 Validated
                </span>
              </div>
              <div className="rounded-xl border border-line bg-card p-4">
                <span className="text-muted block text-[10px] uppercase font-bold">Regulatory Target</span>
                <span className="text-sub font-semibold text-xs mt-2 block">
                  Digital Personal Data Protection Act, 2023
                </span>
              </div>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-sub max-w-md">
                Download an official compliance report containing timestamped logs, hash chains, and legal verification metadata.
              </p>

              <button
                type="button"
                onClick={handleDownloadPdf}
                disabled={isGenerating}
                className="btn-p text-sm font-semibold py-3 px-6 disabled:opacity-50 flex items-center gap-2 shrink-0 cursor-pointer shadow-md"
              >
                {isGenerating ? (
                  <>
                    <Spinner size="sm" /> Generating PDF...
                  </>
                ) : (
                  '🛡️ Download Certificate PDF'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
